import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';

/**
 * Servicio de almacenamiento seguro usando Capacitor Preferences
 * 
 * Características de seguridad por plataforma:
 * - iOS: Usa Keychain (encriptado nativamente)
 * - Android: Usa EncryptedSharedPreferences (encriptado nativamente)
 * - Web: Usa localStorage (sin encriptar, pero con mejor aislamiento)
 */
@Injectable({
  providedIn: 'root'
})
export class SecureStorageService {

  /**
   * Almacena un valor de forma segura
   * @param key Clave para el valor
   * @param value Valor a almacenar (puede ser string, objeto, etc.)
   */
  async set(key: string, value: any): Promise<void> {
    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
      await Preferences.set({
        key,
        value: stringValue
      });
    } catch (error) {
      console.error('Error al guardar en almacenamiento seguro:', error);
      throw error;
    }
  }

  /**
   * Obtiene un valor del almacenamiento seguro
   * @param key Clave del valor a obtener
   * @returns El valor almacenado o null si no existe
   */
  async get(key: string): Promise<string | null> {
    try {
      const { value } = await Preferences.get({ key });
      return value;
    } catch (error) {
      console.error('Error al obtener del almacenamiento seguro:', error);
      return null;
    }
  }

  /**
   * Obtiene un valor y lo parsea como JSON
   * @param key Clave del valor a obtener
   * @returns El objeto parseado o null si no existe
   */
  async getObject<T>(key: string): Promise<T | null> {
    try {
      const value = await this.get(key);
      if (value) {
        return JSON.parse(value) as T;
      }
      return null;
    } catch (error) {
      console.error('Error al parsear objeto del almacenamiento seguro:', error);
      return null;
    }
  }

  /**
   * Elimina un valor del almacenamiento seguro
   * @param key Clave del valor a eliminar
   */
  async remove(key: string): Promise<void> {
    try {
      await Preferences.remove({ key });
    } catch (error) {
      console.error('Error al eliminar del almacenamiento seguro:', error);
      throw error;
    }
  }

  /**
   * Limpia todo el almacenamiento seguro
   */
  async clear(): Promise<void> {
    try {
      await Preferences.clear();
    } catch (error) {
      console.error('Error al limpiar almacenamiento seguro:', error);
      throw error;
    }
  }

  /**
   * Obtiene todas las claves almacenadas
   * @returns Array de claves
   */
  async keys(): Promise<string[]> {
    try {
      const { keys } = await Preferences.keys();
      return keys;
    } catch (error) {
      console.error('Error al obtener claves del almacenamiento seguro:', error);
      return [];
    }
  }

  /**
   * Migra datos desde localStorage a almacenamiento seguro
   * @param keys Array de claves a migrar
   */
  async migrateFromLocalStorage(keys: string[]): Promise<void> {
    try {
      for (const key of keys) {
        const value = localStorage.getItem(key);
        if (value) {
          await this.set(key, value);
          localStorage.removeItem(key);
        }
      }
      console.log('Migración de localStorage completada');
    } catch (error) {
      console.error('Error durante la migración:', error);
      throw error;
    }
  }
}
